import React, { useState, useEffect } from "react";
import Footer from "../layout/Footer";
import { useNotification } from "../NotificationContext";
import PublicHeader from "../layout/PublicHeader";
import "./Feedback.css";

const Feedback = () => {
  const { notify } = useNotification();
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });

  const INTERVIEW_ID = localStorage.getItem("interviewId");
  const BASE_URL = process.env.REACT_APP_REST_BASE_URL;

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (window.innerWidth - e.pageX * 2) / 50;
      const y = (window.innerHeight - e.pageY * 2) / 50;
      setBgPosition({ x, y });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const handleRatingClick = (value) => setRating(value);

  const handleSubmit = () => {
    if (!feedback.trim()) {
      notify("error", "Please write some feedback before submitting.");
      return;
    }

    if (rating === 0) {
      notify("error", "Please rate your experience before submitting.");
      return;
    }

    setSubmitted(true);

    fetch(`${BASE_URL}/interview_ai/submit-feedback/${INTERVIEW_ID}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ feedback, rating }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.message) console.error("Feedback failed:", data);
      })
      .catch((err) => console.error("Network error:", err));
  };

  const maxChars = 800;
  const remaining = maxChars - feedback.length;
  const backgroundStyle = {
    "--feedback-x": `${50 + bgPosition.x}%`,
    "--feedback-y": `${50 + bgPosition.y}%`,
  };

  const GlobeIcon = () => (
    <svg className="tt-feedback-globe" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9.5" fill="#fff" />
      <path d="M12 2.5c2.6 2.6 3.9 5.8 3.9 9.5s-1.3 6.9-3.9 9.5M12 2.5C9.4 5.1 8.1 8.3 8.1 12s1.3 6.9 3.9 9.5M2.8 9.5h18.4M2.8 14.5h18.4" stroke="#3A5BC7" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );

  if (submitted) {
    return (
      <div className="tt-feedback-shell">
        <PublicHeader />
        <main className="tt-feedback-page tt-feedback-success" style={backgroundStyle}>
          <div className="tt-feedback-decor tt-feedback-decor-one" aria-hidden="true" />
          <div className="tt-feedback-decor tt-feedback-decor-two" aria-hidden="true" />
          <section className="tt-feedback-success-card">
            <div className="tt-feedback-success-icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="tt-feedback-eyebrow">VIRIQ0 HIRES · CANDIDATE EXPERIENCE</p>
            <h1>Thank <span>You!</span></h1>
            <p className="tt-feedback-success-lede">Your valuable feedback has been received! We've sent your interview report to the management.</p>
            <div className="tt-feedback-next">
              <span className="tt-feedback-next-mark" aria-hidden="true">✦</span>
              <p><strong>What's next?</strong> Our team will review your feedback to continuously improve the interview experience. You can expect to hear back about next steps within 2-3 business days.</p>
            </div>
            <a href="/" className="tt-feedback-button tt-feedback-primary tt-feedback-home">
              <GlobeIcon /> Visit Viriqo Hires Homepage
              <svg className="tt-feedback-arrow" width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </a>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const checklist = [
    "Your responses are completely anonymous",
    "Takes less than 2 minutes to complete",
    "Help us improve the platform for future candidates",
    "All suggestions are carefully reviewed by our team",
  ];

  return (
    <div className="tt-feedback-shell">
      <PublicHeader />
      <main className="tt-feedback-page" style={backgroundStyle}>
        <div className="tt-feedback-decor tt-feedback-decor-one" aria-hidden="true" />
        <div className="tt-feedback-decor tt-feedback-decor-two" aria-hidden="true" />
        <div className="tt-feedback-dots" aria-hidden="true" />
        <div className="tt-feedback-wrap">
          <section className="tt-feedback-card">
            <div className="tt-feedback-intro">
              <div className="tt-feedback-tile-wrap" aria-hidden="true">
                <span className="tt-feedback-tile">
                  <svg width="54" height="54" viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="17" stroke="currentColor" strokeWidth="3.5" /><path className="tt-feedback-clock-hand" d="M24 24V12" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" /><path d="M24 24l8 5" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" /></svg>
                </span>
                <svg className="tt-feedback-spark" viewBox="0 0 40 40" fill="none"><path d="M10 4v10M18 20l8-8M24 30h12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
              </div>
              <p className="tt-feedback-eyebrow">VIRIQO HIRES · CANDIDATE EXPERIENCE</p>
              <h1>Share Your <span>Experience</span></h1>
              <p className="tt-feedback-lede">Your honest feedback helps us create better interview experiences for everyone. Tell us what worked and what we can improve.</p>

              <ul className="tt-feedback-checks">
                {checklist.map((item) => (
                  <li key={item}>
                    <span className="tt-feedback-check" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3.5 9.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href="/" className="tt-feedback-button tt-feedback-primary tt-feedback-visit">
                <GlobeIcon /> Visit Viriqo Hires
                <svg className="tt-feedback-arrow" width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </a>
            </div>

            <span className="tt-feedback-divider" aria-hidden="true" />

            <div className="tt-feedback-form">
              <div className="tt-feedback-rating">
                <h2>Rate your interview experience</h2>
                <div className="tt-feedback-stars" role="radiogroup" aria-label="Rate your interview experience">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      className={`tt-feedback-star ${star <= (hoverRating || rating) ? "is-active" : ""}`}
                      role="radio"
                      aria-checked={rating === star}
                      aria-label={`${star} ${star === 1 ? "star" : "stars"}`}
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z" /></svg>
                    </button>
                  ))}
                  <span className="tt-feedback-rating-label" aria-live="polite">{rating > 0 ? `${rating}/5` : "Select rating"}</span>
                </div>
              </div>

              <div className="tt-feedback-detail">
                <label htmlFor="tt-feedback-text">Your detailed feedback</label>
                <textarea
                  id="tt-feedback-text"
                  placeholder="What did you think about the interview process? Were the questions clear? How was the AI interviewer? Any technical issues or suggestions for improvement?"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  maxLength={maxChars}
                />
                <div className="tt-feedback-counter-row">
                  <span className={remaining < 50 ? "is-low" : ""}>{remaining} characters remaining</span>
                  {remaining < 100 && <span className="tt-feedback-limit">Getting close to character limit</span>}
                </div>
              </div>

              <div className="tt-feedback-actions">
                <button className="tt-feedback-button tt-feedback-primary tt-feedback-submit" onClick={handleSubmit} disabled={!feedback.trim() || rating === 0}>Submit Feedback <svg className="tt-feedback-arrow" width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></button>
                <button className="tt-feedback-button tt-feedback-clear" onClick={() => { setFeedback(""); setRating(0); }}>Clear All</button>
              </div>
              {(!feedback.trim() || rating === 0) && (
                <p className="tt-feedback-hint">
                  {!feedback.trim() && rating === 0
                    ? "Please provide both a rating and feedback to submit."
                    : !feedback.trim()
                    ? "Please write some feedback before submitting."
                    : "Please rate your experience before submitting."}
                </p>
              )}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Feedback;
